import * as React from 'react';
import { MessageBar, MessageBarType, Pivot, PivotItem, Spinner, SpinnerSize } from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { ISharePointOrganizerProps } from './ISharePointOrganizerProps';
import type { ILibraryInfo } from '../../../models/LibraryInfo';
import type { IScanResult } from '../../../models/ScanResult';
import type { IHealthReport, IssueCategory } from '../../../models/Issue';
import type { IChangeDecision, IOrganizationPlan, IPlanPreview } from '../../../models/Plan';
import { generatePlan } from '../../../plan/PlanGenerator';
import { type IResolvedChange, resolveChange, simulatePlan } from '../../../plan/PlanSimulator';
import { ChangeEngine } from '../../../engine/ChangeEngine';
import { analyzeScan } from '../../../rules/RulesEngine';
import { LibraryScanner } from '../../../services/ScannerService';
import { ScanCancelledError } from '../../../services/GraphService';
import { describeGraphError } from '../../../utils/errors';
import LibrarySelector from './LibrarySelector';
import ScanSummary from './ScanSummary';
import FolderTree from './FolderTree';
import HealthDashboard from './HealthDashboard';
import IssueList from './IssueList';
import PlanView from './PlanView';
import BeforeAfterView from './BeforeAfterView';
import ApplyPanel from './ApplyPanel';
import HistoryView from './HistoryView';
import IntakeView from './IntakeView';
import TagsView from './TagsView';
import StructureView from './StructureView';
import { readTags } from '../../../tagging/readTags';

export default function SharePointOrganizer(props: ISharePointOrganizerProps): React.ReactElement {
  const { graphService, siteId, siteTitle, ruleOptions, auditLog, userName, allowChanges, telemetry, ai, tagging } = props;
  const scanner: LibraryScanner = React.useMemo(() => new LibraryScanner(graphService), [graphService]);

  const [libraries, setLibraries] = React.useState<ILibraryInfo[] | undefined>(undefined);
  const [loadError, setLoadError] = React.useState<string | undefined>(undefined);
  const [selectedId, setSelectedId] = React.useState<string | undefined>(undefined);
  const [scanning, setScanning] = React.useState<boolean>(false);
  const [itemsRead, setItemsRead] = React.useState<number>(0);
  const [scanError, setScanError] = React.useState<string | undefined>(undefined);
  const [result, setResult] = React.useState<IScanResult | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = React.useState<IssueCategory | undefined>(undefined);
  const [decisions, setDecisions] = React.useState<{ [changeId: string]: IChangeDecision }>({});
  const [previewPending, setPreviewPending] = React.useState<boolean>(true);
  /** Frozen when the Apply panel opens, so later clicks can't change what's being applied. */
  const [applying, setApplying] = React.useState<{ engine: ChangeEngine; changes: IResolvedChange[] } | undefined>(undefined);
  const cancelRequested: React.MutableRefObject<boolean> = React.useRef<boolean>(false);

  // Rules are cheap and deterministic, so re-run them whenever the scan or settings change.
  const report: IHealthReport | undefined = React.useMemo(
    () => (result ? analyzeScan(result, ruleOptions) : undefined),
    [result, ruleOptions]
  );
  const plan: IOrganizationPlan | undefined = React.useMemo(
    () => (result && report ? generatePlan(result, report, ruleOptions) : undefined),
    [result, report, ruleOptions]
  );
  React.useEffect(() => {
    if (plan && report) {
      const byType: { [type: string]: number } = {};
      for (const c of plan.changes) {
        byType[c.type] = (byType[c.type] ?? 0) + 1;
      }
      telemetry.event('plan.generated', { issues: report.issues.length, changes: plan.changes.length, ...byType });
    }
  }, [plan, report, telemetry]);
  // Always includes pending changes: conflict warnings on each card must reflect the whole plan.
  const fullPreview: IPlanPreview | undefined = React.useMemo(
    () => (result && plan ? simulatePlan(result, plan, decisions, true) : undefined),
    [result, plan, decisions]
  );
  const approvedPreview: IPlanPreview | undefined = React.useMemo(
    () => (result && plan && !previewPending ? simulatePlan(result, plan, decisions, false) : undefined),
    [result, plan, decisions, previewPending]
  );

  const decide = React.useCallback((changeId: string, decision: IChangeDecision): void => {
    setDecisions(prev => ({ ...prev, [changeId]: decision }));
  }, []);
  const approveMany = React.useCallback((changeIds: string[]): void => {
    setDecisions(prev => {
      const next: { [changeId: string]: IChangeDecision } = { ...prev };
      for (const id of changeIds) {
        next[id] = { ...(prev[id] ?? {}), status: 'approved' };
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    let active: boolean = true;
    graphService.getLibraries(siteId)
      .then(libs => {
        if (!active) { return; }
        setLibraries(libs);
        if (libs.length > 0) {
          setSelectedId(libs[0].id);
        }
      })
      .catch(error => {
        telemetry.error('libraries.failed', error);
        if (active) { setLoadError(describeGraphError(error)); }
      });
    return () => {
      active = false;
      cancelRequested.current = true;
    };
  }, [graphService, siteId]);

  const startScan = React.useCallback((): void => {
    const library: ILibraryInfo | undefined = libraries?.filter(l => l.id === selectedId)[0];
    if (!library) { return; }

    cancelRequested.current = false;
    setScanning(true);
    setItemsRead(0);
    setScanError(undefined);
    setResult(undefined);
    setSelectedCategory(undefined);
    setDecisions({});

    telemetry.event('scan.started', { libraryId: library.id });
    scanner.scan(library, p => setItemsRead(p.itemsRead), () => cancelRequested.current)
      .then(scanResult => {
        telemetry.event('scan.finished', {
          libraryId: library.id,
          files: scanResult.fileCount,
          folders: scanResult.folderCount,
          maxDepth: scanResult.maxDepth,
          quarantinedFiles: scanResult.managedItemCount,
          protectedFolders: Object.keys(scanResult.protectedFolders).length,
          durationMs: scanResult.durationMs
        });
        setResult(scanResult);
        setScanning(false);
      })
      .catch(error => {
        if (error instanceof ScanCancelledError) {
          telemetry.event('scan.cancelled', { libraryId: library.id });
        } else {
          telemetry.error('scan.failed', error, { libraryId: library.id });
          setScanError(describeGraphError(error));
        }
        setScanning(false);
      });
  }, [libraries, selectedId, scanner, telemetry]);

  const openApply = React.useCallback((): void => {
    if (!result || !plan) { return; }
    const changes: IResolvedChange[] = plan.changes
      .map(c => resolveChange(result, c, decisions[c.id]))
      .filter(r => r.status === 'approved' && !r.error);
    const engine: ChangeEngine = new ChangeEngine({
      scan: result,
      plan,
      writer: graphService.createDriveWriter(result.library.id),
      audit: auditLog,
      user: userName
    });
    setApplying({ engine, changes });
  }, [result, plan, decisions, graphService, auditLog, userName]);

  const cancelScan = React.useCallback((): void => {
    cancelRequested.current = true;
  }, []);

  return (
    <section className={styles.sharePointOrganizer}>
      <header className={styles.header}>
        <h2 className={styles.title}>SharePoint Organizer</h2>
        <div className={styles.subtitle}>{siteTitle}</div>
      </header>

      {loadError && <MessageBar messageBarType={MessageBarType.error} isMultiline>{loadError}</MessageBar>}

      {!libraries && !loadError && <Spinner size={SpinnerSize.medium} label="Loading document libraries…" />}

      {libraries && libraries.length === 0 && (
        <MessageBar>This site has no document libraries.</MessageBar>
      )}

      {libraries && libraries.length > 0 && (
        <LibrarySelector
          libraries={libraries}
          selectedId={selectedId}
          scanning={scanning}
          itemsRead={itemsRead}
          onSelect={id => { setSelectedId(id); setResult(undefined); setScanError(undefined); }}
          onScan={startScan}
          onCancel={cancelScan}
        />
      )}

      {scanError && <MessageBar messageBarType={MessageBarType.error} isMultiline>{scanError}</MessageBar>}

      {result && report && (
        <>
          <ScanSummary result={result} />
          <Pivot className={styles.tabs}>
            <PivotItem headerText="Health report" itemIcon="Health">
              <HealthDashboard report={report} selected={selectedCategory} onSelect={setSelectedCategory} />
              {selectedCategory && report.byCategory[selectedCategory].length > 0 && (
                <IssueList scan={result} category={selectedCategory} issues={report.byCategory[selectedCategory]} />
              )}
            </PivotItem>
            {plan && fullPreview && (
              <PivotItem headerText="Organization plan" itemIcon="ClipboardList" itemCount={plan.changes.length}>
                <PlanView
                  scan={result}
                  plan={plan}
                  decisions={decisions}
                  preview={fullPreview}
                  onDecide={decide}
                  onApproveMany={approveMany}
                  onReset={() => setDecisions({})}
                  allowChanges={allowChanges}
                  onApply={openApply}
                  ai={ai}
                  onDecideMany={many => setDecisions(prev => ({ ...prev, ...many }))}
                />
              </PivotItem>
            )}
            {plan && fullPreview && (
              <PivotItem headerText="Before / after" itemIcon="BranchCompare">
                <BeforeAfterView
                  libraryName={result.library.name}
                  preview={previewPending ? fullPreview : (approvedPreview ?? fullPreview)}
                  includePending={previewPending}
                  onIncludePendingChange={setPreviewPending}
                />
              </PivotItem>
            )}
            <PivotItem headerText="Intake" itemIcon="Inbox" itemCount={result.intake ? result.intake.files.length : undefined}>
              <IntakeView
                scan={result}
                auditLog={auditLog}
                createWriter={() => graphService.createDriveWriter(result.library.id)}
                userName={userName}
                allowChanges={allowChanges}
                telemetry={telemetry}
                onRescan={startScan}
              />
            </PivotItem>
            <PivotItem headerText="Structure" itemIcon="Org">
              <StructureView
                scan={result}
                auditLog={auditLog}
                createWriter={() => graphService.createDriveWriter(result.library.id)}
                loadTags={tagging ? ids => readTags(tagging.createMetadata(result.library.id), ids) : undefined}
                userName={userName}
                allowChanges={allowChanges}
                telemetry={telemetry}
                onRescan={startScan}
              />
            </PivotItem>
            {tagging && (
              <PivotItem headerText="Tags" itemIcon="Tag">
                <TagsView scan={result} tagging={tagging} auditLog={auditLog} userName={userName} allowChanges={allowChanges} telemetry={telemetry} />
              </PivotItem>
            )}
            <PivotItem headerText="History" itemIcon="History">
              <HistoryView
                library={result.library}
                auditLog={auditLog}
                createWriter={() => graphService.createDriveWriter(result.library.id)}
                createMetadata={tagging ? () => tagging.createMetadata(result.library.id) : undefined}
                userName={userName}
                allowChanges={allowChanges}
                telemetry={telemetry}
                onRescan={startScan}
              />
            </PivotItem>
            <PivotItem headerText="Folder structure" itemIcon="FabricFolder">
              <FolderTree result={result} />
            </PivotItem>
          </Pivot>
        </>
      )}

      {applying && result && plan && (
        <ApplyPanel
          scan={result}
          plan={plan}
          changes={applying.changes}
          engine={applying.engine}
          auditLogUrl={auditLog.viewUrl}
          telemetry={telemetry}
          onDismiss={() => setApplying(undefined)}
          onRescan={() => { setApplying(undefined); startScan(); }}
        />
      )}
    </section>
  );
}
